import type { Route, Stop } from '@day';

/**
 * The Stops page grouped (board `routes-web`): by route, or by courier across their routes today. Pure: the page passes
 * what matches (the tab, filters and search) and every stop, and draws what comes back.
 */
export type GroupBy = 'none' | 'route' | 'courier';

export interface StopGroup {
  /** `route:<id>`, `courier:<uid>`, or `sent`: by route, the stops sent on their own (no route yet). */
  key: string;
  kind: 'route' | 'courier' | 'sent';
  /** The route's id, or the courier's uid; '' for `sent`. */
  id: string;
  riderId: string | null;
  /** The group's routes today, running first (as Live lists them). */
  routes: Route[];
  /** What matches, in the route's order (or the column sort, when the owner picked one). */
  rows: Stop[];
  /** Every stop the group has today, and how many of them are delivered. */
  total: number;
  done: number;
}

/** Running first, then waiting, then done: Live's order (LiveTables.tsx). */
const ORDER: Record<Route['state'], number> = { running: 0, declined: 1, waiting: 2, done: 3, ended: 3 };
const rankOf = (r: Route | null) => (r ? ORDER[r.state] : 9);
const startOf = (r: Route | null) => r?.startedAt ?? r?.sentAt ?? 0;
const byRoute = (a: Route | null, b: Route | null) => rankOf(a) - rankOf(b) || startOf(a) - startOf(b);

/**
 * [rows] (what matches) grouped [by] route or courier; a group with nothing matching isn't there. [all]: every stop today,
 * for each group's total. [rank]: a courier's place in Live's list (an unknown courier goes last). [sorted]: the owner
 * sorted by a column, so rows keep [rows]' order inside each group instead of the route's.
 */
export function groupStops(
  rows: Stop[],
  all: Stop[],
  by: 'route' | 'courier',
  o: { route: (id: string) => Route | null; rank: (uid: string) => number; sorted?: boolean },
): StopGroup[] {
  const groupOf = (s: Stop) => (by === 'courier' ? `courier:${s.riderId}` : o.route(s.routeId) ? `route:${s.routeId}` : 'sent');
  const counts = new Map<string, { total: number; done: number }>();
  for (const s of all) {
    const c = counts.get(groupOf(s)) ?? { total: 0, done: 0 };
    c.total++;
    if (s.state === 'done') c.done++;
    counts.set(groupOf(s), c);
  }
  const groups = new Map<string, StopGroup>();
  for (const s of rows) {
    const key = groupOf(s);
    let g = groups.get(key);
    if (!g) {
      const kind = key === 'sent' ? 'sent' : by;
      const c = counts.get(key) ?? { total: 0, done: 0 };
      g = { key, kind, id: kind === 'route' ? s.routeId : kind === 'courier' ? s.riderId : '', riderId: kind === 'sent' ? null : s.riderId, routes: [], rows: [], total: c.total, done: c.done };
      groups.set(key, g);
    }
    g.rows.push(s);
  }
  for (const g of groups.values()) {
    // A courier's routes: those their stops today are on (with nothing matching too: the header adds them all up).
    const ids = new Set((g.kind === 'courier' ? all.filter((s) => s.riderId === g.id) : g.rows).map((s) => s.routeId));
    g.routes = [...ids].map(o.route).filter((r): r is Route => !!r).sort(byRoute);
    if (g.total < g.rows.length) g.total = g.rows.length;
    if (!o.sorted) g.rows.sort((a, b) => byRoute(o.route(a.routeId), o.route(b.routeId)) || a.routeId.localeCompare(b.routeId) || a.n - b.n);
  }
  const rank = (uid: string | null) => {
    const i = uid == null ? -1 : o.rank(uid);
    return i < 0 ? 1e6 : i;
  };
  return [...groups.values()].sort((a, b) => {
    if (a.kind === 'sent' || b.kind === 'sent') return (a.kind === 'sent' ? 1 : 0) - (b.kind === 'sent' ? 1 : 0);
    if (by === 'route') return byRoute(a.routes[0] ?? null, b.routes[0] ?? null) || rank(a.riderId) - rank(b.riderId) || a.id.localeCompare(b.id);
    return rank(a.riderId) - rank(b.riderId) || a.id.localeCompare(b.id);
  });
}
